import { useLanguage } from '@/i18n/language-context';
import { DonorError } from './http';

const en = {
  signInUncertain: 'We could not confirm sign-in. Check its status before entering another code.',
  checkSignIn: 'Check sign-in status',
  browserTitle: 'Continue in a supported browser',
  browserHelp:
    'Use your app’s menu to open this page in Safari, Chrome, Edge or Firefox, or copy the link below. Allow cookies and local storage.',
  browserLocal:
    'Start before giving. Basket drafts and access to existing guest payments stay in the original browser. You can open a sign-in link in another supported browser and confirm your email there.',
  copyLink: 'Copy safe link',
  copiedLink: 'Link copied. Paste it in your browser.',
  copyManually: 'Select and copy the link below.',
  openBrowser: 'Open page in browser',

  donate: 'Give',
  basket: 'My gifts',
  account: 'My account',
  accountIntro:
    'Your giving, thoughtfully organized. Manage your gifts and personal details in one place.',
  giftDetails: 'Gift details',
  givingNavigation: 'Giving and account',
  signIn: 'Sign in',
  signOut: 'Sign out',
  continueGuest: 'Continue as guest',
  optionalAccount: 'You can give without an account. An account keeps your history and documents.',
  signInIntro: 'View your gift history and documents.',
  signInEmailHint:
    'We’ll email you a link, no password needed. Sign in to your existing account, or we’ll create one for you.',
  signInEmailExample: 'name@example.com',
  signInOr: 'or',
  findChurch: 'Find a church or organization',
  search: 'Search by name',
  chooseChurch: 'My church',
  amount: 'Amount',
  note: 'Optional note',
  addGift: 'Add a gift',
  addAnother: 'Add another gift',
  remove: 'Remove',
  save: 'Save',
  saved: 'Changes saved.',
  review: 'Review donation',
  pay: 'Give',
  total: 'Total',
  currency: 'Currency',
  email: 'Email',
  optionalEmail: 'Receipt email (optional)',
  firstName: 'First name',
  lastName: 'Last name',
  optional: 'Optional',
  emptyBasket: 'You have not added any gifts yet.',
  noResults: 'No results found.',
  loading: 'Loading…',
  retry: 'Try again',
  back: 'Back',
  browse: 'Browse',
  cancel: 'Cancel payment',
  cancelAction: 'Cancel',
  replaceGifts: 'Replace gifts',
  resume: 'Resume payment',
  refresh: 'Refresh status',
  reference: 'Reference',
  preparing: 'Preparing payment',
  open: 'Payment awaits your confirmation',
  processing: 'Payment is being verified',
  completed: 'Thank you! Your payment is confirmed.',
  cancelled: 'Payment cancelled.',
  cancelling: 'Cancellation is being verified',
  reviewRequired: 'Payment needs review. Do not give again.',
  unknown: 'We cannot verify the result yet. Do not give again.',
  recover: 'Recover original payment',
  pendingExists: 'An unfinished payment exists. Check it before starting another.',
  retired: 'The payment was not created. You can review your gifts again.',
  receiptPreparing: 'Documents are being prepared. Your payment is confirmed.',
  receipts: 'Documents',
  receipt: 'Receipt',
  creditNote: 'Credit note',
  creditReversal: 'Credit reversal',
  download: 'Download',
  emailDelivery: 'Receipts will be sent to the email supplied before payment.',
  guestAccessExpired:
    'Guest access is unavailable. Use the original browser with its cookies, or sign in if you already added this donation to your account. Keep this reference for support. Do not give again.',
  guestLimit: 'Guest access lasts up to 7 days while this browser keeps its cookies.',
  claim: 'Add this donation to my account',
  claimConfirm: 'I confirm adding this donation to the displayed account.',
  claimed: 'This donation is now in your account, including on mobile.',
  consent: 'I confirm that I have read the privacy policy for this donation.',
  privacyPolicy: 'Privacy policy',
  changeReview: 'Your gifts or destinations changed. Review again before giving.',
  unavailable: 'Online payment is currently unavailable.',
  network: 'Connection unavailable. Check the current status before retrying an action.',
  verification: 'Browser verification failed. Please try again.',
  storage: 'Cookies and local storage are required for safe payment. Open a supported browser.',
  error: 'The action could not be completed. Check the status and try again.',
  rateLimit: 'Too many attempts. Please wait before trying again.',
  invalidAmount: 'Enter a valid amount with up to two decimal places.',
  wrongAccount: 'This payment belongs to another identity. Use the original account or browser.',
  requestLink: 'Send sign-in link',
  checkEmail: 'Check your email',
  linkSent: 'We sent a sign-in link to',
  linkContinue: 'Open the link in your email.',
  confirmEmail: 'Confirm your email address',
  confirmEmailHint: 'To sign in here, enter the email address that received this link.',
  continueSignIn: 'Continue signing in',
  linkEmailMismatch: 'This email does not match the link. Check the address and try again.',
  linkSpam: 'Can’t find the email? Check your spam folder too.',
  resendLink: 'Send again',
  changeEmail: 'Change email address',
  linkInvalid: 'The link expired or has already been used. Request a new link.',
  code: 'Authenticator or recovery code',
  verify: 'Verify',
  codeInvalid: 'The code is invalid. Try a current code.',
  signInAgain: 'Sign in again to continue.',
  logoutPending: 'You are signed out in this browser. Server sign-out needs another attempt.',
  history: 'Gift history',
  otherGifts: 'Other gifts',
  giveAgain: 'Give again',
  favorites: 'Favorites',
  favorite: 'Save favorite',
  profile: 'Profile',
  preferences: 'Preferences',
  security: 'Security',
  privacy: 'Data and account',
  previous: 'Previous',
  next: 'Next',
  receiptEmails: 'Email my receipts',
  accountLanguage: 'App and email language',
  twoFactorOn: 'Two-factor authentication is enabled.',
  twoFactorOff: 'Two-factor authentication is not enabled.',
  setup2fa: 'Set up two-factor authentication',
  secret: 'Authenticator setup key',
  recoveryCodes: 'Recovery codes',
  savedCodes: 'I saved these codes in a safe place.',
  confirmSetup: 'Confirm setup',
  regenerate: 'Generate new codes',
  disable2fa: 'Disable two-factor authentication',
  exportData: 'Export my data',
  exportNotice: 'Exports can contain personal data. Download only on a trusted device.',
  expires: 'Expires',
  requestDeletion: 'Request account deletion',
  deletionNotice:
    'Deletion is scheduled after the 14-day grace period. Financial documents are retained under the policy.',
  confirmDeletion: 'I understand and want to delete my account.',
  cancelDeletion: 'Cancel deletion request',
  scheduled: 'Scheduled for',
  pending: 'Pending',
  ready: 'Ready',
  failed: 'Failed',
  expired: 'Expired',
  refunded: 'Refunded',
  refundPending: 'Refund pending',
  partiallyRefunded: 'Partially refunded',
  disputed: 'Disputed payment',
  replaceDraft: 'Replace the gifts in your basket with this selection?',
  confirm: 'Confirm',
  theme: 'Appearance',
  system: 'System',
  light: 'Light',
  dark: 'Dark',
  support: 'Need help?',
  noReceipt: 'No documents are available yet.',
  projectsSupported: 'Projects supported',
  endGuestAccess: 'End guest access',
  endGuestNotice:
    'This removes access to all guest donations in this browser. It does not cancel any payment. Save your documents first.',
  paymentDeclined: 'Payment was declined. Check your details or use another payment method.',
  paymentDetails: 'Check the payment details and complete any bank verification.',
  missingPage: 'This page is unavailable.',
} as const;
export type DonorCopy = { [K in keyof typeof en]: string };
const ro: DonorCopy = {
  signInUncertain:
    'Nu am putut confirma autentificarea. Verifică starea înainte de a introduce alt cod.',
  checkSignIn: 'Verifică autentificarea',
  browserTitle: 'Continuă într-un browser compatibil',
  browserHelp:
    'Din meniul aplicației, deschide pagina în Safari, Chrome, Edge sau Firefox ori copiază adresa de mai jos. Permite modulele cookie și stocarea locală.',
  browserLocal:
    'Continuă înainte de a dărui. Coșul și accesul la plățile fără cont rămân în browserul inițial. Poți deschide linkul de autentificare în alt browser compatibil și confirma adresa de e-mail acolo.',
  copyLink: 'Copiază adresa sigură',
  copiedLink: 'Adresă copiată. Lipește-o în browser.',
  copyManually: 'Selectează și copiază adresa de mai jos.',
  openBrowser: 'Deschide pagina în browser',

  donate: 'Dăruiește',
  basket: 'Darurile mele',
  account: 'Contul meu',
  accountIntro:
    'Darurile tale, într-un singur loc. Gestionează donațiile și detaliile contului tău.',
  giftDetails: 'Detaliile darului',
  givingNavigation: 'Donații și cont',
  signIn: 'Intră în cont',
  signOut: 'Ieși din cont',
  continueGuest: 'Continuă fără cont',
  optionalAccount: 'Poți dărui fără cont. Contul îți păstrează istoricul și documentele.',
  signInIntro: 'Vezi istoricul darurilor și documentele tale.',
  signInEmailHint:
    'Îți trimitem un link prin e-mail, fără parolă. Dacă ai cont, intri în el. Dacă nu, îți creăm unul.',
  signInEmailExample: 'nume@gmail.com',
  signInOr: 'sau',
  findChurch: 'Găsește o biserică sau organizație',
  search: 'Caută după nume',
  chooseChurch: 'Biserica mea',
  amount: 'Sumă',
  note: 'Mesaj opțional',
  addGift: 'Adaugă dar',
  addAnother: 'Adaugă alt dar',
  remove: 'Elimină',
  save: 'Salvează',
  saved: 'Modificările au fost salvate.',
  review: 'Verifică donația',
  pay: 'Dăruiește',
  total: 'Total',
  currency: 'Monedă',
  email: 'E-mail',
  optionalEmail: 'E-mail pentru chitanțe (opțional)',
  firstName: 'Prenume',
  lastName: 'Nume',
  optional: 'Opțional',
  emptyBasket: 'Nu ai adăugat încă niciun dar.',
  noResults: 'Nu am găsit rezultate.',
  loading: 'Se încarcă…',
  retry: 'Încearcă din nou',
  back: 'Înapoi',
  browse: 'Explorează',
  cancel: 'Anulează plata',
  cancelAction: 'Renunță',
  replaceGifts: 'Înlocuiește darurile',
  resume: 'Continuă plata',
  refresh: 'Actualizează starea',
  reference: 'Referință',
  preparing: 'Se pregătește plata',
  open: 'Plata așteaptă confirmarea ta',
  processing: 'Plata este în curs de verificare',
  completed: 'Mulțumim! Plata a fost confirmată.',
  cancelled: 'Plata a fost anulată.',
  cancelling: 'Anularea este în curs de verificare',
  reviewRequired: 'Plata necesită verificare. Nu o repeta.',
  unknown: 'Nu putem verifica încă rezultatul. Nu repeta plata.',
  recover: 'Recuperează plata inițială',
  pendingExists: 'Există o plată nefinalizată. Verific-o înainte să începi alta.',
  retired: 'Plata nu a fost creată. Poți verifica din nou darurile.',
  receiptPreparing: 'Documentele se pregătesc. Plata este confirmată.',
  receipts: 'Documente',
  receipt: 'Chitanță',
  creditNote: 'Notă de credit',
  creditReversal: 'Anulare notă de credit',
  download: 'Descarcă',
  emailDelivery: 'Chitanțele vor fi trimise la adresa oferită înainte de plată.',
  guestAccessExpired:
    'Accesul fără cont nu este disponibil. Folosește browserul inițial cu modulele cookie sau intră în cont dacă ai asociat deja această donație. Păstrează referința pentru asistență. Nu repeta plata.',
  guestLimit:
    'Accesul fără cont este disponibil cel mult 7 zile, dacă păstrezi cookie-urile acestui browser.',
  claim: 'Adaugă această donație în cont',
  claimConfirm: 'Confirm că adaug această donație în contul afișat.',
  claimed: 'Donația este acum disponibilă în cont, inclusiv pe mobil.',
  consent: 'Confirm că am citit politica de confidențialitate pentru această donație.',
  privacyPolicy: 'Politica de confidențialitate',
  changeReview: 'Darurile sau destinațiile s-au schimbat. Verifică din nou înainte de plată.',
  unavailable: 'Plata online nu este disponibilă momentan.',
  network: 'Conexiunea nu este disponibilă. Verifică starea înainte să reîncerci o acțiune.',
  verification: 'Verificarea browserului nu a reușit. Încearcă din nou.',
  storage:
    'Pentru o plată sigură sunt necesare cookie-uri și stocare locală. Deschide un browser compatibil.',
  error: 'Acțiunea nu a putut fi finalizată. Verifică starea și încearcă din nou.',
  rateLimit: 'Prea multe încercări. Așteaptă puțin înainte de a reîncerca.',
  invalidAmount: 'Introdu o sumă validă cu cel mult două zecimale.',
  wrongAccount:
    'Plata aparține altei identități. Intră în contul inițial sau revino în browserul inițial.',
  requestLink: 'Trimite linkul de acces',
  checkEmail: 'Verifică e-mailul',
  linkSent: 'Am trimis un link de acces la',
  linkContinue: 'Deschide linkul din e-mail.',
  confirmEmail: 'Confirmă adresa de e-mail',
  confirmEmailHint: 'Pentru a te conecta aici, introdu adresa de e-mail la care ai primit linkul.',
  continueSignIn: 'Continuă autentificarea',
  linkEmailMismatch: 'Adresa nu corespunde acestui link. Verifică adresa și încearcă din nou.',
  linkSpam: 'Nu ai primit e-mailul? Verifică și folderul Spam.',
  resendLink: 'Trimite din nou',
  changeEmail: 'Schimbă adresa de e-mail',
  linkInvalid: 'Linkul a expirat sau a fost deja folosit. Cere un link nou.',
  code: 'Cod de autentificare sau recuperare',
  verify: 'Verifică',
  codeInvalid: 'Codul nu este valid. Încearcă din nou cu un cod curent.',
  signInAgain: 'Intră din nou în cont pentru a continua.',
  logoutPending:
    'Ai ieșit din cont în acest browser. Deconectarea de la server va trebui reîncercată.',
  history: 'Istoricul Darurilor',
  otherGifts: 'Alte daruri',
  giveAgain: 'Dăruiește din nou',
  favorites: 'Favorite',
  favorite: 'Adaugă la favorite',
  profile: 'Profil',
  preferences: 'Preferințe',
  security: 'Securitate',
  privacy: 'Date și cont',
  previous: 'Anterior',
  next: 'Următor',
  receiptEmails: 'Trimite chitanțele prin e-mail',
  accountLanguage: 'Limba aplicației și a e-mailurilor',
  twoFactorOn: 'Autentificarea în doi pași este activă.',
  twoFactorOff: 'Autentificarea în doi pași nu este activă.',
  setup2fa: 'Activează autentificarea în doi pași',
  secret: 'Cheia aplicației de autentificare',
  recoveryCodes: 'Coduri de recuperare',
  savedCodes: 'Am păstrat codurile într-un loc sigur.',
  confirmSetup: 'Confirmă activarea',
  regenerate: 'Generează coduri noi',
  disable2fa: 'Dezactivează autentificarea în doi pași',
  exportData: 'Exportă datele mele',
  exportNotice:
    'Exportul poate conține date personale. Descarcă-l doar pe un dispozitiv de încredere.',
  expires: 'Expiră',
  requestDeletion: 'Solicită ștergerea contului',
  deletionNotice:
    'Ștergerea este programată după perioada de grație de 14 zile. Documentele financiar-contabile se păstrează conform politicii.',
  confirmDeletion: 'Înțeleg efectele și doresc ștergerea contului.',
  cancelDeletion: 'Anulează solicitarea de ștergere',
  scheduled: 'Programat pentru',
  pending: 'În așteptare',
  ready: 'Disponibil',
  failed: 'Nu a reușit',
  expired: 'Expirat',
  refunded: 'Rambursat',
  refundPending: 'Rambursare în curs',
  partiallyRefunded: 'Rambursat parțial',
  disputed: 'Plată contestată',
  replaceDraft: 'Înlocuiești darurile din coș cu această selecție?',
  confirm: 'Confirmă',
  theme: 'Aspect',
  system: 'Sistem',
  light: 'Luminos',
  dark: 'Întunecat',
  support: 'Ai nevoie de ajutor?',
  noReceipt: 'Nu există încă documente disponibile.',
  projectsSupported: 'Proiecte susținute',
  endGuestAccess: 'Încheie accesul fără cont',
  endGuestNotice:
    'Se elimină accesul la toate donațiile fără cont din acest browser. Nu se anulează nicio plată. Salvează întâi documentele.',
  paymentDeclined: 'Plata a fost refuzată. Verifică datele sau folosește altă metodă de plată.',
  paymentDetails: 'Verifică datele de plată și finalizează verificarea băncii.',
  missingPage: 'Această pagină nu este disponibilă.',
};
const hu: DonorCopy = {
  signInUncertain:
    'A bejelentkezés nem erősíthető meg. Új kód megadása előtt ellenőrizze az állapotát.',
  checkSignIn: 'Bejelentkezés ellenőrzése',
  browserTitle: 'Folytatás támogatott böngészőben',
  browserHelp:
    'Az alkalmazás menüjéből nyissa meg az oldalt Safari, Chrome, Edge vagy Firefox böngészőben, vagy másolja ki az alábbi címet. Engedélyezze a sütiket és a helyi tárolást.',
  browserLocal:
    'Adományozás előtt váltson böngészőt. A kosár és a vendégfizetések hozzáférése az eredeti böngészőben marad. A bejelentkezési hivatkozást más támogatott böngészőben is megnyithatja, ahol megerősítheti az e-mail-címét.',
  copyLink: 'Biztonságos cím másolása',
  copiedLink: 'Cím másolva. Illessze be a böngészőbe.',
  copyManually: 'Jelölje ki és másolja az alábbi címet.',
  openBrowser: 'Oldal megnyitása böngészőben',

  donate: 'Adományozz',
  basket: 'Adományaim',
  account: 'Fiókom',
  accountIntro: 'Adományai egy helyen. Kezelje adományait és személyes fiókadatait egyszerűen.',
  giftDetails: 'Az adomány részletei',
  givingNavigation: 'Adományok és fiók',
  signIn: 'Bejelentkezés',
  signOut: 'Kijelentkezés',
  continueGuest: 'Folytatás fiók nélkül',
  optionalAccount:
    'Fiók nélkül is adományozhat. Fiókjában megmaradnak az előzmények és dokumentumok.',
  signInIntro: 'Tekintse meg adományozási előzményeit és dokumentumait.',
  signInEmailHint:
    'E-mailben küldünk egy hivatkozást, jelszó nélkül. Ha már van fiókja, bejelentkezik; ha nincs, létrehozunk egyet.',
  signInEmailExample: 'nev@pelda.hu',
  signInOr: 'vagy',
  findChurch: 'Gyülekezet vagy szervezet keresése',
  search: 'Keresés név szerint',
  chooseChurch: 'Gyülekezetem',
  amount: 'Összeg',
  note: 'Üzenet (nem kötelező)',
  addGift: 'Adomány hozzáadása',
  addAnother: 'Másik adomány hozzáadása',
  remove: 'Eltávolítás',
  save: 'Mentés',
  saved: 'A módosítások mentve.',
  review: 'Adomány ellenőrzése',
  pay: 'Adományozz',
  total: 'Összesen',
  currency: 'Pénznem',
  email: 'E-mail',
  optionalEmail: 'Nyugta e-mail-címe (nem kötelező)',
  firstName: 'Keresztnév',
  lastName: 'Vezetéknév',
  optional: 'Nem kötelező',
  emptyBasket: 'Még nem adott hozzá adományt.',
  noResults: 'Nincs találat.',
  loading: 'Betöltés…',
  retry: 'Újrapróbálkozás',
  back: 'Vissza',
  browse: 'Böngészés',
  cancel: 'Fizetés törlése',
  cancelAction: 'Mégse',
  replaceGifts: 'Adományok cseréje',
  resume: 'Fizetés folytatása',
  refresh: 'Állapot frissítése',
  reference: 'Hivatkozás',
  preparing: 'Fizetés előkészítése',
  open: 'A fizetés az Ön megerősítésére vár',
  processing: 'A fizetés ellenőrzés alatt áll',
  completed: 'Köszönjük! A fizetés megerősítve.',
  cancelled: 'A fizetés törölve.',
  cancelling: 'A törlés ellenőrzés alatt áll',
  reviewRequired: 'A fizetés ellenőrzést igényel. Ne adományozzon újra.',
  unknown: 'Az eredmény még nem ellenőrizhető. Ne adományozzon újra.',
  recover: 'Eredeti fizetés helyreállítása',
  pendingExists: 'Van egy befejezetlen fizetés. Új indítása előtt ellenőrizze.',
  retired: 'A fizetés nem jött létre. Ellenőrizheti újra adományait.',
  receiptPreparing: 'A dokumentumok készülnek. A fizetés megerősítve.',
  receipts: 'Dokumentumok',
  receipt: 'Nyugta',
  creditNote: 'Jóváírás',
  creditReversal: 'Jóváírás visszavonása',
  download: 'Letöltés',
  emailDelivery: 'A nyugtákat a fizetés előtt megadott címre küldjük.',
  guestAccessExpired:
    'A vendéghozzáférés nem érhető el. Használja az eredeti böngészőt a sütikkel, vagy jelentkezzen be, ha az adományt már fiókjához kapcsolta. Őrizze meg a hivatkozást az ügyfélszolgálat számára. Ne adományozzon újra.',
  guestLimit: 'A vendéghozzáférés legfeljebb 7 napig érhető el, amíg a böngésző megőrzi a sütiket.',
  claim: 'Adomány hozzáadása a fiókomhoz',
  claimConfirm: 'Megerősítem, hogy az adományt a megjelenített fiókhoz adom.',
  claimed: 'Az adomány már elérhető a fiókjában, mobilon is.',
  consent: 'Megerősítem, hogy elolvastam az adományra vonatkozó adatvédelmi tájékoztatót.',
  privacyPolicy: 'Adatvédelmi tájékoztató',
  changeReview: 'Az adományok vagy célok megváltoztak. Fizetés előtt ellenőrizze újra.',
  unavailable: 'Az online fizetés jelenleg nem érhető el.',
  network: 'Nincs kapcsolat. Művelet ismétlése előtt ellenőrizze az állapotot.',
  verification: 'A böngésző ellenőrzése sikertelen. Próbálja újra.',
  storage:
    'A biztonságos fizetéshez sütik és helyi tárhely szükséges. Nyisson meg egy támogatott böngészőt.',
  error: 'A művelet nem sikerült. Ellenőrizze az állapotot, és próbálja újra.',
  rateLimit: 'Túl sok próbálkozás. Kérjük, várjon az újrapróbálkozás előtt.',
  invalidAmount: 'Adjon meg érvényes összeget, legfeljebb két tizedesjeggyel.',
  wrongAccount:
    'A fizetés másik személyazonossághoz tartozik. Használja az eredeti fiókot vagy böngészőt.',
  requestLink: 'Bejelentkezési hivatkozás küldése',
  checkEmail: 'Ellenőrizze e-mailjét',
  linkSent: 'Bejelentkezési hivatkozást küldtünk erre a címre:',
  linkContinue: 'Nyissa meg az e-mailben kapott hivatkozást.',
  confirmEmail: 'Erősítse meg az e-mail-címét',
  confirmEmailHint: 'A bejelentkezéshez adja meg azt az e-mail-címet, amelyre a hivatkozást kapta.',
  continueSignIn: 'Bejelentkezés folytatása',
  linkEmailMismatch: 'Az e-mail-cím nem egyezik a hivatkozással. Ellenőrizze, és próbálja újra.',
  linkSpam: 'Nem érkezett meg az e-mail? Ellenőrizze a levélszemét mappát is.',
  resendLink: 'Újraküldés',
  changeEmail: 'E-mail-cím módosítása',
  linkInvalid: 'A hivatkozás lejárt vagy már felhasználták. Kérjen újat.',
  code: 'Hitelesítő vagy helyreállítási kód',
  verify: 'Ellenőrzés',
  codeInvalid: 'Érvénytelen kód. Próbáljon egy aktuális kódot.',
  signInAgain: 'A folytatáshoz jelentkezzen be újra.',
  logoutPending:
    'Ebben a böngészőben kijelentkezett. A szerverről való kijelentkezést újra kell próbálni.',
  history: 'Adományozási előzmények',
  otherGifts: 'Egyéb adományok',
  giveAgain: 'Újabb adomány',
  favorites: 'Kedvencek',
  favorite: 'Hozzáadás a kedvencekhez',
  profile: 'Profil',
  preferences: 'Beállítások',
  security: 'Biztonság',
  privacy: 'Adatok és fiók',
  previous: 'Előző',
  next: 'Következő',
  receiptEmails: 'Nyugták küldése e-mailben',
  accountLanguage: 'Az alkalmazás és az e-mailek nyelve',
  twoFactorOn: 'A kétlépcsős hitelesítés aktív.',
  twoFactorOff: 'A kétlépcsős hitelesítés nem aktív.',
  setup2fa: 'Kétlépcsős hitelesítés beállítása',
  secret: 'Hitelesítő beállítási kulcs',
  recoveryCodes: 'Helyreállítási kódok',
  savedCodes: 'Biztonságos helyre mentettem a kódokat.',
  confirmSetup: 'Beállítás megerősítése',
  regenerate: 'Új kódok létrehozása',
  disable2fa: 'Kétlépcsős hitelesítés kikapcsolása',
  exportData: 'Adataim exportálása',
  exportNotice: 'Az export személyes adatokat tartalmazhat. Csak megbízható eszközre töltse le.',
  expires: 'Lejárat',
  requestDeletion: 'Fiók törlésének kérése',
  deletionNotice:
    'A törlést a 14 napos türelmi idő után hajtjuk végre. A pénzügyi dokumentumokat a szabályzat szerint őrizzük meg.',
  confirmDeletion: 'Megértettem a következményeket, és törölni kívánom a fiókomat.',
  cancelDeletion: 'Törlési kérelem visszavonása',
  scheduled: 'Ütemezve',
  pending: 'Függőben',
  ready: 'Elérhető',
  failed: 'Sikertelen',
  expired: 'Lejárt',
  refunded: 'Visszatérítve',
  refundPending: 'Visszatérítés folyamatban',
  partiallyRefunded: 'Részben visszatérítve',
  disputed: 'Vitatott fizetés',
  replaceDraft: 'Lecseréli a kosárban lévő adományokat erre a választásra?',
  confirm: 'Megerősítés',
  theme: 'Megjelenés',
  system: 'Rendszer',
  light: 'Világos',
  dark: 'Sötét',
  support: 'Segítségre van szüksége?',
  noReceipt: 'Még nincsenek elérhető dokumentumok.',
  projectsSupported: 'Támogatott projektek',
  endGuestAccess: 'Vendéghozzáférés befejezése',
  endGuestNotice:
    'Ezzel megszűnik az összes vendégadomány elérése ebben a böngészőben. Nem töröl fizetést. Előbb mentse a dokumentumokat.',
  paymentDeclined:
    'A fizetést elutasították. Ellenőrizze az adatokat, vagy válasszon másik fizetési módot.',
  paymentDetails: 'Ellenőrizze a fizetési adatokat, és végezze el a banki ellenőrzést.',
  missingPage: 'Ez az oldal nem érhető el.',
};
export function useDonorCopy(): { t: DonorCopy } {
  const { locale } = useLanguage();
  return { t: { ro, en, hu }[locale] };
}
export function describeError(error: unknown, t: DonorCopy): string {
  if (!(error instanceof DonorError)) return t.error;
  if (error.code === 'PAYMENT_DECLINED') return t.paymentDeclined;
  if (['PAYMENT_DETAILS_INVALID', 'PAYMENT_VERIFICATION_FAILED'].includes(error.code))
    return t.paymentDetails;
  if (error.code === 'INVALID_AMOUNT') return t.invalidAmount;
  if (error.status === 429) return t.rateLimit;
  if (error.code === 'NETWORK_UNAVAILABLE') return t.network;
  if (['STORAGE_REQUIRED', 'BROWSER_UNSUPPORTED', 'GUEST_SESSION_UNAVAILABLE'].includes(error.code))
    return t.storage;
  if (error.code.includes('BROWSER_VERIFICATION')) return t.verification;
  if (error.code === 'WEB_CHECKOUT_DISABLED') return t.unavailable;
  if (['GIVING_ACTOR_CHANGED', 'SESSION_INVALID'].includes(error.code)) return t.wrongAccount;
  if (error.code === 'AUTH_2FA_CODE_INVALID') return t.codeInvalid;
  if (error.code === 'AUTH_LINK_EMAIL_MISMATCH') return t.linkEmailMismatch;
  if (error.code.startsWith('AUTH_LINK_')) return t.linkInvalid;
  if (error.code === 'CHECKOUT_IN_PROGRESS') return t.pendingExists;
  if (
    ['QUOTE_EXPIRED', 'POLICY_CHANGED', 'DESTINATIONS_CHANGED', 'PROJECT_UNAVAILABLE'].includes(
      error.code,
    )
  )
    return t.changeReview;
  if (error.status === 401) return t.signInAgain;
  return t.error;
}
